import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useHttpClientContext } from '@/shared/context/HttpClientContext';
import { Location, LocationSchema } from '@/modules/location/domain/LocationSchema';
import { ApiLocation } from '@/generated/location-client/api-types';

export type LocationSource =
    | { type: 'ORGANIZATION'; id: number }
    | { type: 'CONSTRUCTION_PROJECT'; id: string; organizationId: number };

const transformApiLocationToLocation = (
    apiLocation: Omit<ApiLocation, 'organizationId'> & { organizationId?: number | null }
): Location | null => {
    const openingTimes =
        apiLocation.restriction
            ?.filter(r => r.type === 'openingTimes')
            ?.flatMap(
                r =>
                    r.value?.map(entry => ({
                        start: entry.start,
                        end: entry.end,
                        weekday: entry.dayOfWeekName,
                    })) || []
            ) || [];

    const locationData = {
        ...apiLocation,
        geoAddress: {
            coordinates: apiLocation.geoCoordinates,
            address: {
                ...apiLocation.address,
                street: apiLocation.address?.street ?? null,
                streetNumber: apiLocation.address?.number ?? null,
                zipCode: apiLocation.address?.zip,
            },
        },
        openingTimes,
        transferPoint: null,
        isCustom: false,
        organizationId: apiLocation.organizationId ?? undefined,
        legacyFactoryId: apiLocation.legacyFactoryId,
    };

    const result = LocationSchema.safeParse(locationData);
    return result.success ? result.data : null;
};

export const useLocations = (sources: Array<LocationSource>): Location[] => {
    const { locationClient } = useHttpClientContext();

    return useSuspenseQueries({
        queries: sources.map(source => ({
            queryKey: ['get-locations', source],
            queryFn: async () => {
                switch (source.type) {
                    case 'ORGANIZATION': {
                        return {
                            organizationId: source.id,
                            locations: await locationClient.getLocationsByOrganization({
                                params: { organizationId: source.id },
                            }),
                        };
                    }
                    case 'CONSTRUCTION_PROJECT': {
                        return {
                            organizationId: source.organizationId,
                            locations: await locationClient.getLocationsByConstructionProject({
                                params: { constructionProjectId: source.id },
                            }),
                        };
                    }
                }
            },
        })),
        combine: results => {
            return results
                .flatMap(result => {
                    const organizationId = result.data.organizationId;
                    return result.data.locations.locations.map(location => ({ ...location, organizationId })) ?? [];
                })
                .map(location => transformApiLocationToLocation(location))
                .filter((location): location is Location => location !== null);
        },
    });
};

export function getUseLegacyFactoryIdQueryKey(organizationId?: number, locationId?: string) {
    return ['location', locationId, 'organizationId', organizationId, 'legacy-factory-id'];
}

export const useLegacyFactoryId = (location?: {
    id: string;
    organizationId?: number;
    legacyFactoryId?: number | null;
}) => {
    const { locationClient } = useHttpClientContext();

    const query = useQuery({
        queryKey: getUseLegacyFactoryIdQueryKey(location?.organizationId, location?.id),
        queryFn: () =>
            locationClient.getLocationsByOrganization({
                params: { organizationId: location!.organizationId! },
            }),
        select: data => data.locations.find(l => l.id === location!.id)?.legacyFactoryId,
        enabled: !location?.legacyFactoryId && !!location?.organizationId,
    });

    return { data: query.data ?? location?.legacyFactoryId, isLoading: query.isLoading };
};
